import type {Draft} from 'immer';

import type {MessageNode, MessageTree} from './thread';

/** Add a message as a child of `parentId`; updates cursor and active child. */
export function addMessageNode(
  tree: Draft<MessageTree>,
  parentId: string | null,
  node: MessageNode
): void {
  if (tree.nodes[node.id]) {
    return; // Skip if the message already exists.
  }

  tree.nodes[node.id] = node as Draft<MessageNode>;
  tree.parentId[node.id] = parentId;

  if (parentId === null) {
    tree.rootChildren.push(node.id);
  } else {
    if (!tree.children[parentId]) {
      tree.children[parentId] = [];
    }

    tree.children[parentId].push(node.id);
  }

  if (parentId !== null) {
    tree.activeChildId[parentId] = node.id;
  }

  tree.currentNodeId = node.id;
}

/**
 * findToolCallPart searches message content parts for a `ToolCallPart` matching
 * `toolCallId`. If `hintNodeId` is provided, it is checked first.
 */
export function findToolCallPart(
  tree: MessageTree | Draft<MessageTree>,
  toolCallId: string,
  hintNodeId?: string | null
): {nodeId: string; partIndex: number} | null {
  if (hintNodeId) {
    const result = findToolCallPartInMessage(tree, hintNodeId, toolCallId);
    if (result) {
      return result;
    }
  }

  for (const nodeId of Object.keys(tree.nodes)) {
    if (nodeId === hintNodeId) {
      continue;
    }

    const result = findToolCallPartInMessage(tree, nodeId, toolCallId);
    if (result) {
      return result;
    }
  }

  return null;
}

/**
 * getActiveBranch returns the path of active message nodes for the provided
 * `MessageTree`. If `upToNodeId` is provided, the path stops at that node;
 * otherwise it walks to `currentNodeId` (the leaf).
 */
export function getActiveBranch(
  tree: MessageTree,
  upToNodeId?: string
): MessageNode[] {
  const startId = upToNodeId ?? tree.currentNodeId;
  if (!startId) {
    return [];
  }

  const path: MessageNode[] = [];

  let currentNodeId: string | null = startId;
  while (currentNodeId !== null) {
    const node = tree.nodes[currentNodeId];
    if (!node) {
      break;
    }

    path.push(node);
    currentNodeId = tree.parentId[currentNodeId] ?? null;
  }

  path.reverse();
  return path;
}

/**
 * Switch the active branch to the branch containing `targetNodeId`. First, walk
 * up from `targetNodeId`, updating `activeChildId` at each level. Then, walk
 * down from `targetNodeId` to leaf via `activeChildId`.
 */
export function switchBranch(
  tree: Draft<MessageTree>,
  targetNodeId: string
): void {
  if (!tree.nodes[targetNodeId]) {
    return;
  }

  // Walk up to root, marking each parent's active child along the way.
  let childId = targetNodeId;
  let parentId = tree.parentId[childId] ?? null;
  while (parentId !== null) {
    tree.activeChildId[parentId] = childId;
    childId = parentId;
    parentId = tree.parentId[childId] ?? null;
  }

  // Walk down from target to leaf, following active children.
  let current = targetNodeId;
  let next = tree.activeChildId[current];
  while (next && tree.children[current]?.includes(next)) {
    current = next;
    next = tree.activeChildId[current];
  }

  tree.currentNodeId = current;
}

function findToolCallPartInMessage(
  tree: MessageTree | Draft<MessageTree>,
  nodeId: string,
  toolCallId: string
): {nodeId: string; partIndex: number} | null {
  const node = tree.nodes[nodeId];
  if (!node) {
    return null;
  }

  for (let i = 0; i < node.content.length; i++) {
    const content = node.content[i];
    if (content.type === 'tool-call' && content.toolCallId === toolCallId) {
      return {nodeId, partIndex: i};
    }
  }

  return null;
}
